import { useAxios } from '@vueuse/integrations/useAxios'
import type { AxiosRequestConfig, AxiosRequestHeaders, InternalAxiosRequestConfig } from 'axios'
import { AxiosHeaders } from 'axios'

import instance from './instance'
import { generateRequestKey } from '@/utils/misc-tools'
import type { RbResponse } from '@/types/misc/http'

export interface RequestConfig<T> extends Omit<AxiosRequestConfig<T>, 'params' | 'data'> {
  params?: Record<string, unknown>
  data?: unknown
  skipRequestDedup?: boolean
  authReplayAttempted?: boolean
  skipAuthRenewal?: boolean
}

// * 非必要情况下不要在请求服务内导入该模块，避免循环依赖以及破坏全局单例
// * 1、通过使用 src/composables/use-request.ts 中的 useAxiosRequest 方法获取实例
// * 2、通过 src/composables/global-api.ts 中的 axiosRequest 方法获取实例
export default function useAxiosRequest() {
  const pendingRequest = ref<Map<string, Promise<RbResponse<unknown>>>>(new Map())

  // 发起请求，增加判断当前进来的请求是否已经在 pendingRequest 中，是则直接返回 pendingRequest 中的请求，避免重复请求
  async function useRequest<T>(config: RequestConfig<T>) {
    if (!config.headers) {
      config.headers = new AxiosHeaders() as AxiosRequestHeaders
    }
    const key = generateRequestKey(config as InternalAxiosRequestConfig<T>)
    const shouldDedup = !config.skipRequestDedup

    // 检查是否有相同的请求正在进行
    if (shouldDedup && pendingRequest.value.has(key)) {
      return pendingRequest.value.get(key) as Promise<RbResponse<T>>
    }

    // 发起请求并存储 Promise 对象
    const requestPromise = new Promise<RbResponse<T>>((resolve, reject) => {
      const { execute } = useAxios<RbResponse<T>>(instance)
      const url = config.url ?? ''
      execute(url, config).then((response) => {
        resolve(response.data.value as RbResponse<T>)
      }).catch((error) => {
        reject(error)
      }).finally(() => {
        pendingRequest.value.delete(key)
      })
    })

    if (shouldDedup) {
      pendingRequest.value.set(key, requestPromise as Promise<RbResponse<unknown>>)
    }
    return requestPromise
  }

  // GET 请求
  async function get<T>(url: string, params: Record<string, unknown> = {}, config?: RequestConfig<T>): Promise<RbResponse<T>> {
    return useRequest<T>({ ...config, params, method: 'get', url })
  }

  // POST 请求
  async function post<T>(url: string, data?: unknown, config?: RequestConfig<T>): Promise<RbResponse<T>> {
    return useRequest<T>({ ...config, method: 'post', url, data })
  }

  // DELETE 请求
  async function del<T>(url: string, config?: RequestConfig<T>): Promise<RbResponse<T>> {
    return useRequest<T>({ ...config, method: 'delete', url })
  }

  // PUT 请求
  async function put<T>(url: string, data?: unknown, config?: RequestConfig<T>): Promise<RbResponse<T>> {
    return useRequest<T>({ ...config, method: 'put', url, data })
  }

  return {
    useRequest,
    get,
    post,
    del,
    put,
  }
}
